import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projectFirestore, projectStorage } from "../../firebase-config/config";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import {
  FaCamera,
  FaArrowLeft,
  FaEdit,
  FaSave,
  FaExternalLinkAlt,
  FaCheck,
  FaShare,
  FaTimes,
} from "react-icons/fa";
import TextEditor from "../../default-components/TextEditor";
import Select from "react-select";
import "./BlogDetails.scss";
import PGLoaders from "../PGLoaders/PGLoaders";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const { documents: blogCategories } = useCollection("m_categories", ["source", "==", "blog"]);

  const [blogData, setBlogData] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", link: "" });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategoryOptions, setSelectedCategoryOptions] = useState([]);
  const [selectedSubCategoryOptions, setSelectedSubCategoryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const getCategoryOptions = () => {
    if (!Array.isArray(blogCategories)) return [];
    return blogCategories
      .filter((cat) => String(cat.status).toLowerCase() === "active" && cat.category?.trim())
      .map((cat) => ({
        value: cat.category.trim(),
        label: cat.category.trim(),
      }));
  };

  const getSubCategoryOptions = () => {
    const selectedCategories = selectedCategoryOptions.map((opt) => opt.value);
    let options = [];

    selectedCategories.forEach((categoryValue) => {
      const categoryDoc = blogCategories?.find((cat) => cat.category === categoryValue);
      if (categoryDoc?.subCategories) {
        const subCats = categoryDoc.subCategories
          .filter((subCat) => subCat.status === "active")
          .map((subCat) => ({
            value: subCat.subCategoryName,
            label: subCat.subCategoryName,
            category: categoryValue,
          }));
        options = [...options, ...subCats];
      }
    });

    return options;
  };

  const handleCategoryChange = (selected) => {
    setSelectedCategoryOptions(selected || []);
    const selectedValues = selected?.map((opt) => opt.value) || [];
    const validSubCats = getSubCategoryOptions();
    const updatedSubCats = selectedSubCategoryOptions.filter((subOpt) =>
      validSubCats.find((valid) => valid.value === subOpt.value)
    );
    setSelectedSubCategoryOptions(updatedSubCats);
  };

  const handleSubCategoryChange = (selected) => {
    setSelectedSubCategoryOptions(selected || []);
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setIsLoading(true);
        const doc = await projectFirestore.collection("blogs_and_articles").doc(id).get();
        if (doc.exists) {
          const data = doc.data();
          setBlogData(data);
          setFormData({
            title: data.title,
            description: data.description,
            link: data.link || "",
          });
          setSelectedCategoryOptions(
            data.category?.map((cat) => ({ value: cat, label: cat })) || []
          );
          setSelectedSubCategoryOptions(
            data.subCategory?.map((sub) => ({ value: sub, label: sub })) || []
          );
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching blog:", error);
        setIsLoading(false);
      }
    };

    const fetchRecentBlogs = async () => {
      try {
        const snapshot = await projectFirestore
          .collection("blogs_and_articles")
          .orderBy("createdAt", "desc")
          .limit(5)
          .get();

        const blogs = [];
        snapshot.forEach((doc) => {
          if (doc.id !== id) {
            blogs.push({ id: doc.id, ...doc.data() });
          }
        });

        setRecentBlogs(blogs.slice(0, 4));
      } catch (error) {
        console.error("Error fetching recent blogs:", error);
      }
    };

    fetchBlog();
    fetchRecentBlogs();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
      const reader = new FileReader();
      reader.onload = (event) => {
        setBlogData((prev) => ({ ...prev, imgUrl: event.target.result }));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setShowSuccess(false);
      let updatedData = {
        ...formData,
        category: selectedCategoryOptions.map((opt) => opt.value),
        subCategory: selectedSubCategoryOptions.map((opt) => opt.value),
      };

      if (selectedImage) {
        const storageRef = projectStorage.ref(`blogs_and_articles/${id}`);
        const imageRef = storageRef.child(selectedImage.name);
        const uploadTask = imageRef.put(selectedImage);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error("Upload error:", error);
          },
          async () => {
            const imageUrl = await imageRef.getDownloadURL();
            updatedData.imgUrl = imageUrl;
            await projectFirestore.collection("blogs_and_articles").doc(id).update(updatedData);
            afterSave(updatedData);
          }
        );
      } else {
        await projectFirestore.collection("blogs_and_articles").doc(id).update(updatedData);
        afterSave(updatedData);
      }
    } catch (err) {
      console.error("Error updating blog:", err);
      setIsSaving(false);
    }
  };

  const afterSave = (updatedData) => {
    setIsEditing(false);
    setSelectedImage(null);
    setBlogData((prev) => ({ ...prev, ...updatedData }));
    setIsSaving(false);
    setUploadProgress(0);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleEditorSave = async () => {
    try {
      setIsSaving(true);
      const doc = await projectFirestore.collection("blogs_and_articles").doc(id).get();
      if (doc.exists) {
        const updatedData = doc.data();
        setFormData({
          title: updatedData.title,
          description: updatedData.description,
          link: updatedData.link || "",
        });
        setBlogData(updatedData);
      }
      setIsSaving(false);
      setIsEditing(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Error refreshing saved content:", error);
      setIsSaving(false);
    }
  };

  const shareURL = async () => {
    try {
      await navigator.share({
        title: blogData?.title || "Check out this blog",
        text: blogData?.description.substring(0, 100) || "Interesting blog post",
        url: window.location.href,
      });
    } catch (error) {
      console.error("Sharing failed:", error);
    }
  };

  if (!blogData) return <div className="blog-details-not-found"><PGLoaders/></div>;

  return (
    <div className="blog-details-main-parent-div">
      {showSuccess && (
        <div className="success-notification">
          <div className="success-content">
            <FaCheck className="success-icon" />
            <span>Changes saved successfully!</span>
          </div>
        </div>
      )}

      <div className="blog-details-container">
        <div className="blog-header">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate(-1)}>
              <FaArrowLeft />
            </button>
            {isEditing ? (
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="edit-title-input"
                placeholder="Blog Title"
              />
            ) : (
              <h1>{blogData.title}</h1>
            )}
          </div>
          <div className="header-right">
            {user?.role === "admin" && !isEditing && (
              <button className="edit-button" onClick={() => setIsEditing(true)}>
                <FaEdit /> Edit
              </button>
            )}
          </div>
        </div>

        <div className="blog-main-content">
          <div className="blog-content-left">
            <div className="blog-image-container">
              <img src={blogData.imgUrl} alt="Blog" className="blog-image" />
              {isEditing && (
                <div className="image-upload-overlay">
                  <label className="image-upload-label">
                    <input
                      type="file"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="image-upload-input"
                    />
                    <FaCamera className="camera-icon" />
                    Change Image
                  </label>
                  {selectedImage && (
                    <div className="selected-image-name">
                      {selectedImage.name.length > 15
                        ? `${selectedImage.name.substring(0, 12)}...`
                        : selectedImage.name}
                    </div>
                  )}
                </div>
              )}
              {isSaving && selectedImage && uploadProgress > 0 && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  <span className="progress-text">{uploadProgress.toFixed(0)}% Uploading...</span>
                </div>
              )}

              {blogData.createdAt?.seconds && (
                <div className="date-circle">
                  <span className="day">
                    {new Date(blogData.createdAt.seconds * 1000).toLocaleDateString("en-US", {
                      day: "2-digit",
                    })}
                  </span>
                  <span className="month">
                    {new Date(blogData.createdAt.seconds * 1000).toLocaleDateString("en-US", {
                      month: "short",
                    }).toUpperCase()}
                  </span>
                </div>
              )}

              <button className="share-button" onClick={shareURL}>
                <FaShare /> Share
              </button>
            </div>

            <div className="blog-description-section">
              {isEditing ? (
                <>
                  <TextEditor
                    initialDescription={formData.description}
                    documentId={id}
                    docFieldName="description"
                    collectionName="blogs_and_articles"
                    onSave={handleEditorSave}
                    onCancel={() => setIsEditing(false)}
                  />
                  <div className="edit-category-section">
                    <label>Categories</label>
                    <Select
                      isMulti
                      options={getCategoryOptions()}
                      value={selectedCategoryOptions}
                      onChange={handleCategoryChange}
                      placeholder="Select categories..."
                    />
                  </div>
                  <div className="edit-subcategory-section" style={{ marginTop: "12px" }}>
                    <label>Subcategories</label>
                    <Select
                      isMulti
                      options={getSubCategoryOptions()}
                      value={selectedSubCategoryOptions}
                      onChange={handleSubCategoryChange}
                      isDisabled={selectedCategoryOptions.length === 0}
                      placeholder="Select subcategories..."
                    />
                  </div>
                  <div className="link-input-container">
                    <input
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      className="edit-link-input"
                      placeholder="External Link (optional)"
                    />
                    {formData.link && (
                      <a
                        href={formData.link.startsWith("http") ? formData.link : `https://${formData.link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-preview"
                      >
                        <FaExternalLinkAlt />
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="blog-description">
                    {blogData.description ? (
                      <div dangerouslySetInnerHTML={{ __html: blogData.description }} />
                    ) : (
                      <p>No description available</p>
                    )}
                  </div>
                  {blogData.category && (
                    <p><strong>Category:</strong> {Array.isArray(blogData.category) ? blogData.category.join(", ") : blogData.category}</p>
                  )}
                  {blogData.subCategory && (
                    <p><strong>Subcategory:</strong> {Array.isArray(blogData.subCategory) ? blogData.subCategory.join(", ") : blogData.subCategory}</p>
                  )}
                  {blogData.link && (
                    <a
                      href={blogData.link.startsWith("http") ? blogData.link : `https://${blogData.link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Read Full Article <FaExternalLinkAlt />
                    </a>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="blog-sidebar">
            <div className="recent-posts-section">
              <h3>RECENT POSTS</h3>
              <div className="recent-posts-grid">
                {recentBlogs.length === 0 ? (
                  <div className="no-recent-blogs">No recent posts found</div>
                ) : (
                  recentBlogs.map((blog) => (
                    <div
                      key={blog.id}
                      className="recent-post-card"
                      onClick={() => navigate(`/blog-details/${blog.id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="post-image-wrapper">
                        <img src={blog.imgUrl} alt={blog.title} className="post-image" />
                      </div>
                      <div className="post-content">
                        <h4 className="post-title">{blog.title}</h4>
                        <span className="post-date">
                          {blog.createdAt?.seconds
                            ? new Date(blog.createdAt.seconds * 1000).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })
                            : ""}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="action-buttons">
            <button className="save-button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <span className="button-loader"></span> : <><FaSave /> Save</>}
            </button>
            <button
              className="cancel-button"
              onClick={() => {
                setIsEditing(false);
                setSelectedImage(null);
                setFormData({
                  title: blogData.title,
                  description: blogData.description,
                  link: blogData.link || "",
                });
                setSelectedCategoryOptions(
                  blogData.category?.map((cat) => ({ value: cat, label: cat })) || []
                );
                setSelectedSubCategoryOptions(
                  blogData.subCategory?.map((sub) => ({ value: sub, label: sub })) || []
                );
              }}
              disabled={isSaving}
            >
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetails;
